import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthnAutofill,
  WebAuthnAbortService,
} from "@simplewebauthn/browser";
import { PASSKEY_CHALLENGE_TTL_MS } from "@shared/passkeyPolicy";
// Every successful ceremony marks THIS DEVICE as holding a SaleFish passkey,
// which is what lets the installed app open on "Unlock" (lib/sessionResume.ts).
import { rememberPasskeyOnThisDevice } from "./sessionResume";

/**
 * Bumped every time something claims the browser's single ceremony slot.
 *
 * `startAuthentication()` installs a NEW abort signal on every call, which
 * cancels whatever ceremony is already running (see createNewAbortSignal in
 * @simplewebauthn/browser). So an autofill ceremony that finishes fetching its
 * options a moment AFTER the user pressed "Sign in with a passkey" does not
 * merely start late — it kills the prompt the user is looking at, and
 * loginWithPasskey() reports it as "No passkey on this device".
 *
 * The epoch is how the autofill notices it was superseded while awaiting the
 * network, at the one point it can still stand down.
 */
let ceremonyEpoch = 0;

/**
 * The ceremony the browser is currently holding, as a promise that SETTLES —
 * never rejects — when the browser has let go of it.
 *
 * The abort signal is a request, not a receipt. `controller.abort()` returns
 * immediately; the browser tears the ceremony down on its own schedule, and
 * until it has, its one ceremony slot is still occupied. Starting the next
 * `navigator.credentials.get()` inside that window is the failure this module
 * has been chasing: on Safari the second call is refused or simply never opens
 * a prompt, so the button "does nothing" — and a full page load fixes it,
 * because a new document gets a new slot. That is the shape of the bug report
 * that brought us here: after signing out, the passkey button does nothing
 * until the address bar is hit.
 *
 * Tracking the promise is what lets releaseCeremonySlot() wait for the receipt.
 */
let outstandingCeremony: Promise<void> | null = null;

/** Run a WebAuthn ceremony while the module knows the slot is occupied. */
function trackCeremony<T>(ceremony: Promise<T>): Promise<T> {
  // Settles either way: a torn-down ceremony rejects, and that IS the receipt.
  const settled = ceremony.then(
    () => {},
    () => {},
  );
  outstandingCeremony = settled;
  void settled.then(() => {
    // Only clear our own entry — a newer ceremony may already have replaced it.
    if (outstandingCeremony === settled) outstandingCeremony = null;
  });
  return ceremony;
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * How long to wait for the browser to acknowledge a cancelled ceremony.
 *
 * Capped, because a browser that never settles an aborted ceremony must not
 * take the sign-in button down with it — a late start is recoverable, a button
 * that never calls get() at all is not.
 */
const CEREMONY_TEARDOWN_TIMEOUT_MS = 1000;

/**
 * Cancel any in-flight WebAuthn ceremony (e.g. the login page's conditional-UI autofill
 * get()). Safari allows only ONE active navigator.credentials call at a time and is strict
 * about it — a lingering ceremony makes a subsequent create()/get() silently abort ("stuck").
 * Chrome is more forgiving. Call this before starting a new ceremony, and on route changes.
 *
 * This only ASKS. Anything that is about to start its own ceremony must call
 * releaseCeremonySlot() and await it instead.
 */
export function cancelWebAuthnCeremony() {
  ceremonyEpoch += 1;
  try {
    WebAuthnAbortService.cancelCeremony();
  } catch {
    /* no-op */
  }
}

/**
 * Cancel the running ceremony AND wait until the browser has actually released
 * it, so the next `navigator.credentials` call gets a free slot.
 *
 * Callers run this concurrently with fetching their challenge (see
 * loginWithPasskey), so on every normal sign-in the wait is free — the network
 * round trip is dead time the browser can spend tearing the old ceremony down.
 */
export async function releaseCeremonySlot(): Promise<void> {
  const running = outstandingCeremony;
  cancelWebAuthnCeremony();
  if (running) await Promise.race([running, delay(CEREMONY_TEARDOWN_TIMEOUT_MS)]);
  // One macrotask past the promise: the abort resolves in the page's JS, the
  // teardown finishes in the browser's own process, and those are not the same
  // moment.
  await delay(0);
}

/**
 * A ceremony that failed before the user could possibly have decided anything.
 *
 * Browsers report "the user declined" and "this ceremony could not start" with
 * the SAME error (NotAllowedError, deliberately — distinguishing them would
 * leak whether a credential exists). The one thing that does separate them is
 * the clock: a person cannot read a system prompt and dismiss it inside 400ms,
 * so a rejection that fast was never a decision. Those are worth retrying once;
 * a real refusal must never be.
 */
const INSTANT_FAILURE_MS = 400;

function isCeremonyCollision(err: unknown, elapsedMs: number): boolean {
  if (elapsedMs > INSTANT_FAILURE_MS) return false;
  const name = (err as { name?: string } | null)?.name;
  return name === "NotAllowedError" || name === "AbortError" || name === "InvalidStateError";
}

// Plain fetch for the CSRF-EXEMPT public login endpoints.
// api-lint-ok: /auth/passkeys/authenticate/{options,verify} are on the server's
// CSRF exclusion list — the ceremony runs before there is a session to protect.
async function postJSON(url: string, body?: unknown) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error((msg as any).message || "Request failed");
  }
  return res.json();
}

/** Feature-detect WebAuthn. Uses globalThis so it is testable in a node test env. */
export function isPasskeySupported(): boolean {
  return typeof (globalThis as any).PublicKeyCredential === "function";
}

export const supportsAutofill = browserSupportsWebAuthnAutofill;

/** csrfPost: a function the component supplies (wrapping the app's apiRequest) that POSTs/returns JSON. */
type CsrfPost = (url: string, body?: unknown) => Promise<any>;

// Registration (authenticated → CSRF via csrfPost)
export async function registerPasskey(csrfPost: CsrfPost, name?: string) {
  // Clear any lingering ceremony (e.g. conditional-UI autofill) and WAIT for the
  // browser to release it, so Safari has fully torn it down by the time we call
  // create(). Otherwise the create() collides with the in-flight get() and
  // silently aborts. The wait overlaps the options fetch, so it is free.
  // releaseCeremonySlot() cancels SYNCHRONOUSLY and returns a promise for the
  // browser's acknowledgement, so calling it before the fetch is what guarantees
  // the cancel cannot land on top of the ceremony we are about to start.
  const released = releaseCeremonySlot();
  const [options] = await Promise.all([csrfPost("/api/auth/passkeys/register/options"), released]);
  const attResp = await trackCeremony(startRegistration({ optionsJSON: options }));
  const result = await csrfPost("/api/auth/passkeys/register/verify", { ...attResp, name });
  rememberPasskeyOnThisDevice();
  return result;
}

/**
 * Open the modal passkey prompt, and give a ceremony that never opened one more
 * chance.
 *
 * The retry is the difference between "sign out, then sign in with a passkey"
 * working and needing a full page reload. It re-runs the whole approach — a
 * second teardown, a settling pause, a FRESH challenge — because the first
 * attempt's challenge may have been consumed by the ceremony that failed, and
 * because a slot that was still occupied 400ms ago needs longer than zero to
 * come free.
 */
async function openModalCeremony(fetchOptions: () => Promise<any>) {
  // Cancel first (synchronously, inside releaseCeremonySlot), then let the
  // browser's teardown and the challenge fetch run against the same clock.
  const released = releaseCeremonySlot();
  const [options] = await Promise.all([fetchOptions(), released]);
  const startedAt = Date.now();
  try {
    return await trackCeremony(startAuthentication({ optionsJSON: options }));
  } catch (err) {
    if (!isCeremonyCollision(err, Date.now() - startedAt)) throw err;
    await releaseCeremonySlot();
    await delay(INSTANT_FAILURE_MS);
    const retryOptions = await fetchOptions();
    return await trackCeremony(startAuthentication({ optionsJSON: retryOptions }));
  }
}

// Passwordless login (public; CSRF-exempt)
export async function loginWithPasskey(email?: string) {
  const authResp = await openModalCeremony(() =>
    postJSON("/api/auth/passkeys/authenticate/options", email ? { email } : {}),
  );
  const result = await postJSON("/api/auth/passkeys/authenticate/verify", authResp);
  rememberPasskeyOnThisDevice();
  return result;
}

/**
 * A passkey the user actually presented, that the server then refused.
 *
 * The autofill ceremony has exactly one outcome worth interrupting anyone
 * over, and this is it. Everything else it can produce — an ignored prompt, a
 * deliberate teardown, a browser that cannot do conditional UI — is not a
 * failure the user experienced, and a toast for any of those would fire on
 * ordinary page loads. Reserving a type for the one loud case is what lets the
 * caller stay quiet about the rest without going quiet about everything, which
 * is how a stale ceremony went unnoticed until it was reported from the field.
 */
export class PasskeyVerificationError extends Error {
  constructor(message?: string) {
    super(message || "The server would not accept that passkey");
    this.name = "PasskeyVerificationError";
  }
}

/**
 * Conditional UI / autofill (public; CSRF-exempt). Call on login mount when
 * supportsAutofill() is true.
 *
 * Deduped at module scope, deliberately. Every call issues a NEW server-side
 * challenge, and the login page unmounts and remounts whenever AuthContext
 * flips isLoading (it swaps the whole tree for a loader), so a plain
 * useEffect fired this repeatedly. Production, 2026-08-11: eight challenge
 * requests in 19 seconds and zero verifies — each one stranding the ceremony
 * before it.
 *
 * Remounts join the existing ceremony instead of starting a rival one — but
 * only for as long as its challenge is redeemable. The dedupe is what a
 * REMOUNT needs; it must not outlive the SESSION the challenge was issued
 * into, or the login page comes back holding a ceremony nothing can verify.
 * That is why the entry is stamped and bounded by PASSKEY_CHALLENGE_TTL_MS
 * rather than "until the promise settles", which for an ignored conditional-UI
 * prompt is never.
 */
let autofill: { promise: Promise<unknown>; startedAt: number } | null = null;

/**
 * Forget the parked autofill ceremony and cancel whatever ceremony is running.
 *
 * Call this when the SESSION ends, not when the login page unmounts. Every
 * ceremony holds a challenge issued into one session, and both signing in and
 * signing out replace that session — so the moment the user is signed out,
 * nothing still in flight can ever be redeemed, autofill or otherwise (a
 * half-finished "add a passkey" in Account → Security included).
 *
 * This matters because signing out for inactivity is the ONE path back to the
 * login page that is an SPA transition rather than a page load
 * (AuthContext.logout does setLocation('/login'); every other 401 path goes
 * through window.location.replace('/api/logout')). Module state survives it,
 * so without this the login page re-renders around a ceremony left over from
 * before the user ever signed in.
 */
export function resetPasskeyAutofill() {
  autofill = null;
  cancelWebAuthnCeremony();
}

export async function startPasskeyAutofill() {
  // Join a ceremony only while the challenge it is holding is still redeemable.
  // Past the server's TTL, joining it means awaiting a promise whose only
  // possible outcomes are "the user ignores it" and "400 Authentication
  // expired" — and, worse, issuing no fresh challenge, so the passkey the user
  // picks out of the browser's autofill does nothing at all.
  if (autofill && Date.now() - autofill.startedAt < PASSKEY_CHALLENGE_TTL_MS) {
    return autofill.promise;
  }
  // A ceremony past that point is not just useless, it is occupying the
  // browser's one slot. Tear it down before starting its replacement.
  if (autofill) resetPasskeyAutofill();

  const epoch = ceremonyEpoch;
  const promise = (async () => {
    const options = await postJSON("/api/auth/passkeys/authenticate/options", {});
    // Something claimed the ceremony slot while we were fetching — the passkey
    // button, or registration. Stand down rather than aborting its prompt.
    if (epoch !== ceremonyEpoch) throw new Error("Passkey autofill superseded");
    const authResp = await trackCeremony(
      startAuthentication({ optionsJSON: options, useBrowserAutofill: true }),
    );

    // Past this line the user has DONE something: they picked a passkey out of
    // the browser's autofill and their authenticator accepted them. A failure
    // from here is one they WATCHED happen, so the caller must be able to tell
    // it apart from a prompt nobody ever answered — which is the far more
    // common outcome and rightly silent.
    let result: unknown;
    try {
      result = await postJSON("/api/auth/passkeys/authenticate/verify", authResp);
    } catch (err) {
      throw new PasskeyVerificationError((err as Error)?.message);
    }
    rememberPasskeyOnThisDevice();
    return result;
  })();

  autofill = { promise, startedAt: Date.now() };

  try {
    return await promise;
  } finally {
    // Only clear our own entry: a newer ceremony may already have replaced it.
    if (autofill?.promise === promise) autofill = null;
  }
}

// 2FA step-up (authenticated → CSRF via csrfPost)
export async function verify2faWithPasskey(csrfPost: CsrfPost) {
  const authResp = await openModalCeremony(() => csrfPost("/api/auth/passkeys/2fa/options"));
  const result = await csrfPost("/api/auth/passkeys/2fa/verify", authResp);
  rememberPasskeyOnThisDevice();
  return result;
}
